import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LearningTypeList } from '@/components/shared/learning-type-list';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { PageSkeleton } from '@/components/shared/page-skeleton';
import { usePathsByScope } from '@/data/learning-path/path';
import {
  type LearningPathsResponse,
  PathType,
} from '@/data/learning-path/interfaces';
import { useBootstrapData } from '@/data/user/bootstrap-data';
import { useSetTitle } from '@/hooks/use-set-title';

type PathItem = LearningPathsResponse['learning_paths'][number];

const mapToCard = (path: PathItem) => {
  const resourcesCount =
    path.resources_count && path.resources_count > 0
      ? `${path.resources_count} ${path.resources_count === 1 ? 'recurso' : 'recursos'}`
      : '';

  return {
    label: path.title,
    description: path.description ?? '',
    image: path.header_image_url,
    link: `/trilhas/${path.id}`,
    info: resourcesCount,
  };
};

interface WrapperProps {
  scope: PathType;
}

const Wrapper = ({ scope }: WrapperProps) => {
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<PathItem[]>([]);
  const { bootstrap } = useBootstrapData();
  const companyName = bootstrap?.info?.colaborador?.empresa?.empresa;

  const { paths, metadata, isFetching } = usePathsByScope({ scope, page });

  const title =
    scope === 'global'
      ? 'Roteiros de Aprendizagem Galena'
      : `Trilhas ${companyName || 'da Empresa'}`;

  const allPaths = page === 1 ? paths : [...accumulated, ...paths];
  const data = allPaths.map(mapToCard);

  const handleVerMais = () => {
    setAccumulated(allPaths);
    setPage((p) => p + 1);
  };

  return (
    <Box>
      <Typography variant="h2" color="primary.dark" mb={2}>
        {title}
      </Typography>
      <LearningTypeList data={data} isLoading={isFetching && page === 1} />
      {!metadata?.last_page && (
        <Stack alignItems="flex-start" mt={3}>
          <Button
            variant="outlined"
            onClick={handleVerMais}
            disabled={isFetching}
          >
            Ver mais
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export const LearningPath = () => {
  useSetTitle('Trilhas');
  const { paths: companyPaths, isFetching } = usePathsByScope({
    scope: PathType.Company,
    page: 1,
  });

  const hasCompanyPaths = companyPaths.length > 0;

  if (isFetching) return <PageSkeleton />;

  return (
    <PageContainer>
      <PageHeader
        title="Trilhas"
        description="Descubra conteúdos organizados na ordem que potencializa seu desenvolvimento."
      />
      <Stack gap={3}>
        {hasCompanyPaths && <Wrapper scope={PathType.Company} />}
        <Wrapper scope={PathType.Global} />
      </Stack>
    </PageContainer>
  );
};
